/**
 * Vessel Certificates ekranının görünümleri (plan M7 Task 27a, 27b; ADR 0029 K28–K31, K34).
 * `@felix/db` `vessel-certificate-views.ts` ve `pms/class.ts` görünümlerinin yapısal kopyasıdır:
 * arayüz veritabanı paketine bağımlı değildir. Ofis ve gemi aynı görünümü okur; gemi ayrıntısında
 * sayfaların gemideki durumu da vardır (K34).
 */

export type CertificateStatus = 'valid' | 'expiring' | 'expired';
export type ReminderStage = 90 | 60 | 30 | 0;
export type SurveyScheme = 'hssc' | 'ism_smc' | 'ism_doc' | 'isps' | 'mlc' | 'none';
export type SurveyWindowKind = 'annual' | 'intermediate' | 'renewal';
export type WindowState = 'upcoming' | 'open' | 'overdue';

/** Sertifika ağacının kökleri (referans ekran "Certificate Tracking"). */
export const CERTIFICATE_CATEGORIES = [
  'statutory',
  'class',
  'safety',
  'environmental',
  'flag_port',
  'other',
] as const;
export type CertificateCategory = (typeof CERTIFICATE_CATEGORIES)[number];

/** Survey Schedule satırları (PMS belgesi §19) ve klas sörveyleri. */
export const SURVEY_KINDS = [
  'annual',
  'intermediate',
  'special',
  'bottom',
  'boiler',
  'safety_equipment',
  'radio',
  'tailshaft',
  'other',
] as const;
export type SurveyKind = (typeof SURVEY_KINDS)[number];

export const ENDORSEMENT_KINDS = ['annual', 'intermediate', 'additional'] as const;
export type EndorsementKind = (typeof ENDORSEMENT_KINDS)[number];

export const CLASS_FINDING_KINDS = ['coc', 'memorandum', 'recommendation'] as const;
export type ClassFindingKind = (typeof CLASS_FINDING_KINDS)[number];

export const CLASS_ITEM_CATEGORIES = [
  'machinery',
  'electrical',
  'pressure_vessel',
  'boiler',
  'tailshaft',
  'other',
] as const;

export const SURVEY_PRINCIPLES = ['pms_ce', 'surveyor', 'condition_monitoring', 'none'] as const;

export type ClassDueState =
  | 'never_surveyed'
  | 'not_credited'
  | 'awaiting_confirmation'
  | 'confirmed'
  | 'overdue';

export interface CertificateType {
  id: string;
  code: string;
  name: string;
  category: string;
  subCategory: string | null;
  surveyScheme: SurveyScheme;
  sortOrder: number;
  rowVersion: number;
}

export type CertificateTypeRef = Pick<
  CertificateType,
  'id' | 'code' | 'name' | 'category' | 'subCategory'
>;

export interface SurveyWindow {
  kind: SurveyWindowKind;
  due: string;
  from: string;
  to: string;
  source: 'computed' | 'override';
  reason: string | null;
}

export type NextSurvey = SurveyWindow & { state: WindowState };

/** Yetki: ofiste sertifika modülü, gemide kayıt (vize, teyit, gemide düzenlenen) (K28). */
export interface CertificateCan {
  create?: boolean;
  edit?: boolean;
  void?: boolean;
  record?: boolean;
}

export interface CertificateListItem {
  id: string;
  typeId: string;
  /** Ofisin sertifikası ya da ofis kaydı gelmemiş gemide düzenlenen sertifika (K28). */
  source: 'office' | 'on_board';
  /** Yenilenmiş ya da gemide yerine yenisi düzenlenmiş: geçmişte kalır, sayılmaz. */
  superseded: boolean;
  officeEntryPending: boolean;
  status: CertificateStatus;
  /** Bitişe kalan gün (bitiş günü 0); süresiz belgede `null`. */
  daysLeft: number | null;
  number: string;
  name: string;
  issuedBy: string;
  issueDate: string;
  expiryDate: string | null;
  type: CertificateTypeRef | null;
  nextSurvey: NextSurvey | null;
  reminder: ReminderStage | null;
  pageCount: number;
  rowVersion: number | null;
  /**
   * Yerine geçtiği önceki sertifika (ADR 0116 §3): listede eskiler güncelin altında "History"
   * olarak gruplanır. Eski gemi sunucusunda yok.
   */
  previousId?: string | null;
}

export interface UpcomingDue {
  id: string;
  source: 'office' | 'on_board';
  dueOn: string;
  /** `expiry` ya da sörvey penceresinin türü. */
  kind: string;
}

export interface CertificateList {
  items: CertificateListItem[];
  summary: { valid: number; expiring: number; overdue: number; total: number };
  upcoming: UpcomingDue[];
  /** Geminin bugünkü yerel günü (CLAUDE.md "Zaman"). */
  today: string;
  /** "Expiring" eşiği (gün; şirket ayarı, K30); eski sunucuda yoksa 30. */
  expiringDays?: number;
  can?: CertificateCan;
}

/** Sayfanın gemideki durumu (K34); ofiste yok. */
export type PageOnBoard = 'on_board' | 'downloading' | 'not_on_board';

export interface CertificatePage {
  id: string;
  sortOrder: number;
  fileName: string;
  size: number;
  mimeType: string;
  sha256: string;
  rowVersion: number;
  onBoard?: PageOnBoard;
  downloadReason?: string | null;
}

export interface Endorsement {
  id: string;
  surveyKind: string;
  endorsedOn: string;
  place: string;
  surveyor: string;
  remarks: string | null;
  createdBy: string;
  createdAt: string;
  satisfies: SurveyWindow | null;
  warning: 'ENDORSEMENT_OUTSIDE_WINDOW' | null;
}

export interface OnBoardIssuance {
  id: string;
  typeId: string;
  replacesCertificateId: string | null;
  number: string;
  issuedBy: string;
  issueDate: string;
  expiryDate: string;
  shortTerm: boolean;
  remarks: string | null;
  createdBy: string;
  createdAt: string;
  linkedCertificateId: string | null;
}

export interface CertificateDetail {
  id: string;
  vesselId: string;
  number: string;
  name: string;
  issuedBy: string;
  issueDate: string;
  expiryDate: string | null;
  anniversaryDate: string | null;
  nextSurveyOverride: string | null;
  windowFromOverride: string | null;
  windowToOverride: string | null;
  overrideReason: string | null;
  renewedFromId: string | null;
  onBoardIssuanceId: string | null;
  /** Notlar: her satır `YYYY-MM-DD Ad Soyad: metin` ("Add Remark"). */
  remarks: string | null;
  rowVersion: number;
  deletedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  type: CertificateType | null;
  status: CertificateStatus;
  daysLeft: number | null;
  superseded: boolean;
  reminder: ReminderStage | null;
  windows: SurveyWindow[];
  nextSurvey: NextSurvey | null;
  pages: CertificatePage[];
  endorsements: Endorsement[];
  issuance: OnBoardIssuance | null;
  chain: {
    id: string;
    number: string;
    issueDate: string;
    expiryDate: string | null;
    deleted: boolean;
  }[];
  can?: CertificateCan;
}

export interface VesselSurvey {
  id: string;
  kind: SurveyKind;
  lastDoneOn: string | null;
  nextDueOn: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  overrideReason: string | null;
  reportAttachmentKey: string | null;
  remarks: string | null;
  rowVersion: number;
  state: WindowState | null;
}

export interface ClassFinding {
  id: string;
  kind: string;
  number: string;
  text: string;
  imposedOn: string;
  dueOn: string | null;
  closedOn: string | null;
  equipmentId: string | null;
  equipmentCode: string | null;
  rowVersion: number;
  overdue: boolean;
}

export interface CeAuthorization {
  id: string;
  userId: string;
  userName: string | null;
  validFrom: string;
  validTo: string | null;
  declarationRef: string;
  rowVersion: number;
  validToday: boolean;
}

export interface ClassItem {
  id: string;
  itemCode: string;
  description: string;
  category: string;
  surveyPrinciple: string;
  intervalMonths: number | null;
  rowVersion: number;
  ceAllowed: boolean;
}

export interface ClassItemList {
  items: ClassItem[];
  /** Kalem listesinde olmayan BV kalem kodlu ekipman (`UNKNOWN_BV_ITEM`). */
  warnings: {
    equipmentId: string;
    bvItemCode: string;
    code: 'UNKNOWN_BV_ITEM';
    equipmentCode: string | null;
  }[];
  can?: CertificateCan;
}

export interface ClassDueRow {
  itemId: string;
  itemCode: string;
  description: string;
  category: string;
  surveyPrinciple: string;
  intervalMonths: number | null;
  ceAllowed: boolean;
  equipment: { id: string; code: string; name: string }[];
  jobs: { id: string; code: string; title: string }[];
  lastCeSurvey: {
    workRecordId: string;
    doneOn: string;
    ceCredit: boolean;
    ceRejectionCode: string | null;
  } | null;
  confirmation: { id: string; confirmedOn: string; surveyor: string } | null;
  state: ClassDueState;
  dueOn: string | null;
}

export interface ClassConfirmation {
  id: string;
  surveyor: string;
  confirmedOn: string;
  remarks: string | null;
  items: { classItemCode: string; workRecordId: string }[];
  createdBy: string;
  createdAt: string;
}

export interface ClassItemImportReport {
  rows: number;
  errors: { row: number; column: string | null; code: string }[];
  added: {
    row: number;
    itemCode: string;
    description: string;
    category: string;
    surveyPrinciple: string;
    intervalMonths: number | null;
  }[];
  updated: { row: number; itemCode: string; fields: string[] }[];
  unchanged: number;
  missing: string[];
  applied: boolean;
}

/** Klas bulgusunun ekipman seçeneği ve yetki beyanının kişisi. */
export interface EquipmentChoice {
  id: string;
  code: string;
  name: string;
}

export interface PersonChoice {
  id: string;
  name: string;
  role: string | null;
}

/* ---------- Girdiler (sunucu şemalarıyla aynı alanlar; `@felix/domain`) ---------- */

export interface CertificateInput {
  typeId: string;
  number: string;
  name: string;
  issuedBy: string;
  issueDate: string;
  expiryDate: string | null;
  anniversaryDate: string | null;
  nextSurveyOverride: string | null;
  windowFromOverride: string | null;
  windowToOverride: string | null;
  overrideReason: string | null;
  renewedFromId: string | null;
  onBoardIssuanceId: string | null;
}

export interface SurveyInput {
  kind: SurveyKind;
  lastDoneOn: string | null;
  nextDueOn: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  overrideReason: string | null;
  remarks: string | null;
}

export interface ClassFindingInput {
  kind: ClassFindingKind;
  number: string;
  text: string;
  imposedOn: string;
  dueOn: string | null;
  closedOn: string | null;
  equipmentId: string | null;
}

export interface CeAuthorizationInput {
  userId: string;
  validFrom: string;
  validTo: string | null;
  declarationRef: string;
}

export interface EndorsementInput {
  surveyKind: EndorsementKind;
  endorsedOn: string;
  place: string;
  surveyor: string;
  remarks: string | null;
}

export interface ConfirmationInput {
  surveyor: string;
  confirmedOn: string;
  items: { classItemCode: string; workRecordId: string }[];
  remarks: string | null;
}

export interface IssuanceInput {
  typeId: string;
  replacesCertificateId: string | null;
  number: string;
  issuedBy: string;
  issueDate: string;
  expiryDate: string;
  shortTerm: boolean;
  remarks: string | null;
}
